import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, onSnapshot, doc, deleteDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './MembersPage.css'; // reuse table + dialog styles

const ALL = 'All';
const STATUSES = ['upcoming', 'soon', 'completed', 'cancelled'];

const _months = ['Jan','Feb','Mar','Apr','May','Jun',
                 'Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtEventDate(dateStr, timeStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dateOut = `${d.getDate()} ${_months[d.getMonth()]} ${d.getFullYear()}`;
  return timeStr ? `${dateOut} · ${timeStr}` : dateOut;
}

const STATUS_COLORS = {
  upcoming:  '#22C55E',
  soon:      '#D4B574',
  completed: '#9A9A9A',
  cancelled: '#E63946',
};

export default function EventsPage() {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [status,  setStatus]  = useState(ALL);
  const [toast,   setToast]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'events'),
      (snap) => {
        setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  // Sort by date descending - most-recent / upcoming first.
  const sorted = useMemo(() => {
    return [...events].sort((a, b) => {
      const da = new Date(a.date || 0).getTime();
      const dbb = new Date(b.date || 0).getTime();
      return dbb - da;
    });
  }, [events]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sorted.filter(ev => {
      const matchSearch = !q
        || ev.title?.toLowerCase().includes(q)
        || ev.location?.toLowerCase().includes(q)
        || ev.district?.toLowerCase().includes(q);
      const matchStatus = status === ALL || ev.status === status;
      return matchSearch && matchStatus;
    });
  }, [sorted, search, status]);

  const changeStatus = useCallback(async (event, newStatus) => {
    try {
      await updateDoc(doc(db, 'events', event.id), {
        status:     newStatus,
        updated_at: serverTimestamp(),
      });
      showToast(`${event.title} → ${newStatus}.`, 'success');
    } catch (e) {
      showToast(`Failed: ${e.message}`, 'error');
    }
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'events', deleteTarget.id));
      showToast(`${deleteTarget.title} removed.`, 'success');
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Events</h1>
          <p className="page-subtitle">
            {events.length} total ·{' '}
            {events.filter(e => e.status === 'upcoming').length} upcoming
          </p>
        </div>
        <Link to="/events/add" className="mem-add-btn">+ Add Event</Link>
      </div>

      {toast && (
        <div className={`mem-toast mem-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="mem-filters">
        <input
          type="search"
          className="mem-search"
          placeholder="Search title, location, district…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="mem-select"
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          {[ALL, ...STATUSES].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="mem-loading">
          <span className="tf-spinner tf-spinner--red" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="mem-empty">No events match this filter.</div>
      ) : (
        <div className="mem-table-wrap">
          <table className="mem-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Date</th>
                <th>Location</th>
                <th>District</th>
                <th>Attendees</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(ev => (
                <tr key={ev.id}>
                  <td>
                    <div className="mem-name-cell">
                      {ev.cover_image_url ? (
                        <img
                          src={ev.cover_image_url}
                          alt=""
                          className="mem-avatar"
                          style={{ objectFit: 'cover', background: '#000' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="mem-avatar">📅</div>
                      )}
                      <div>
                        <div className="mem-name">{ev.title}</div>
                        <div className="mem-phone"
                             style={{
                               maxWidth: 240,
                               overflow: 'hidden',
                               textOverflow: 'ellipsis',
                               whiteSpace: 'nowrap',
                             }}>
                          {ev.description?.slice(0, 80)}
                          {ev.description?.length > 80 ? '…' : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>{fmtEventDate(ev.date, ev.time)}</td>
                  <td>{ev.location || '—'}</td>
                  <td>{ev.district || '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {ev.attendee_count ?? 0}
                  </td>
                  <td>
                    <select
                      value={ev.status || 'upcoming'}
                      onChange={(e) => changeStatus(ev, e.target.value)}
                      style={{
                        background: 'transparent',
                        border: `1px solid ${STATUS_COLORS[ev.status] || '#9A9A9A'}`,
                        color:  STATUS_COLORS[ev.status] || '#9A9A9A',
                        borderRadius: 5,
                        padding: '4px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                      }}
                    >
                      {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <button
                      className="mem-delete-btn"
                      onClick={() => setDeleteTarget(ev)}
                      title="Delete event"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <div className="mem-dialog-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="mem-dialog" onClick={e => e.stopPropagation()}>
            <h3 className="mem-dialog-title">Delete Event?</h3>
            <p className="mem-dialog-body">
              <strong>{deleteTarget.title}</strong> will be permanently removed
              from the Events tab in the mobile app.
              {(deleteTarget.attendee_count ?? 0) > 0 && (
                <>
                  {' '}It currently has{' '}
                  <strong>{deleteTarget.attendee_count}</strong> attendees.
                </>
              )}
            </p>
            <div className="mem-dialog-actions">
              <button className="mem-dialog-cancel"
                onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="mem-dialog-confirm"
                onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
